import * as React from 'react';
import { useAuth } from '../auth';
import { Button } from '../ui.jsx';
import { ProviderAccessLoading } from './provider-access-ui.jsx';
import {
  ClassroomEditors,
  FreshnessPanel,
  ProfileBasicsEditor,
  PublishBar,
  WorkspaceHeader,
} from './provider-workspace-ui.jsx';

const emptyProfile = {
  hours: '',
  languages: '',
  curriculumSummary: '',
  acceptsInPersonTours: true,
  acceptsVirtualTours: false,
};

const newClassroom = () => ({
  localId: crypto.randomUUID(),
  name: '',
  ageGroup: 'Infant',
  schedule: 'Full time',
  capacity: 8,
  enrolled: 8,
  openingDate: '',
  waitlistCount: 0,
});

const formFromWorkspace = (workspace) => ({
  profile: workspace.profile
    ? {
        ...workspace.profile,
        languages: workspace.profile.languages.join(', '),
      }
    : emptyProfile,
  classrooms: workspace.classrooms.length
    ? workspace.classrooms.map((classroom) => ({
        ...classroom,
        localId: classroom.id,
      }))
    : [newClassroom()],
  status: workspace.availability.status || 'live',
});

export const ProviderWorkspaceScreen = ({ go, providerId }) => {
  const auth = useAuth();
  const [workspace, setWorkspace] = React.useState(null);
  const [form, setForm] = React.useState(null);
  const [error, setError] = React.useState('');
  const [receipt, setReceipt] = React.useState('');
  const [loading, setLoading] = React.useState(true);
  const [publishing, setPublishing] = React.useState(false);
  const [confirming, setConfirming] = React.useState(false);

  const load = React.useCallback(async () => {
    if (!auth.account) {
      setLoading(false);
      return;
    }
    setLoading(true);
    setError('');
    try {
      let selectedId = providerId;
      if (!selectedId) {
        const list = await auth.apiFetch('/api/provider-workspaces');
        selectedId = list.data[0]?.provider.id;
      }
      if (!selectedId) {
        throw new Error('No approved provider workspace is available.');
      }
      const response = await auth.apiFetch(
        `/api/provider-workspaces/${selectedId}`,
      );
      setWorkspace(response.data);
      setForm(formFromWorkspace(response.data));
    } catch (requestError) {
      setError(requestError.message);
    } finally {
      setLoading(false);
    }
  }, [auth.account, auth.apiFetch, providerId]);

  React.useEffect(() => {
    load();
  }, [load]);

  const updateProfile = (field, value) =>
    setForm((current) => ({
      ...current,
      profile: { ...current.profile, [field]: value },
    }));

  const updateClassroom = (index, field, value) =>
    setForm((current) => ({
      ...current,
      classrooms: current.classrooms.map((classroom, currentIndex) =>
        currentIndex === index ? { ...classroom, [field]: value } : classroom,
      ),
    }));

  const publish = async () => {
    if (!workspace || !form) return;
    setPublishing(true);
    setError('');
    setReceipt('');
    try {
      const response = await auth.apiFetch(
        `/api/provider-workspaces/${workspace.provider.id}`,
        {
          method: 'PUT',
          body: JSON.stringify({
            expectedVersion: workspace.availability.version,
            status: form.status,
            profile: {
              ...form.profile,
              languages: form.profile.languages
                .split(',')
                .map((language) => language.trim())
                .filter(Boolean),
            },
            classrooms: form.classrooms.map(({ localId, openings, ...room }) => ({
              ...room,
              openingDate: room.openingDate || null,
            })),
          }),
        },
      );
      setWorkspace(response.data);
      setForm(formFromWorkspace(response.data));
      setReceipt(
        `Published version ${response.data.availability.version}. Families now see ${response.data.availability.state === 'live' ? 'current availability' : 'a paused status'}.`,
      );
    } catch (requestError) {
      setError(requestError.message);
    } finally {
      setPublishing(false);
    }
  };

  const confirm = async () => {
    if (!workspace) return;
    setConfirming(true);
    setError('');
    setReceipt('');
    try {
      const response = await auth.apiFetch(
        `/api/provider-workspaces/${workspace.provider.id}/confirm`,
        {
          method: 'POST',
          body: JSON.stringify({
            expectedVersion: workspace.availability.version,
          }),
        },
      );
      setWorkspace(response.data);
      setForm(formFromWorkspace(response.data));
      setReceipt(
        `Confirmed version ${response.data.availability.version}. Availability stays current for seven days.`,
      );
    } catch (requestError) {
      setError(requestError.message);
    } finally {
      setConfirming(false);
    }
  };

  if (auth.loading || loading) {
    return <ProviderAccessLoading>Loading provider workspace…</ProviderAccessLoading>;
  }
  if (!auth.account) {
    return (
      <div className="provider-access-state">
        <h1>Provider sign-in required</h1>
        <Button onClick={auth.signIn}>Sign in</Button>
      </div>
    );
  }
  if (error && (!workspace || !form)) {
    return (
      <div className="provider-access-state is-error">
        <h1>Provider workspace unavailable</h1>
        <p>{error}</p>
        <Button variant="secondary" onClick={() => go('provider-access')}>
          Return to provider account
        </Button>
      </div>
    );
  }

  const totalOpenings = form.classrooms.reduce(
    (sum, classroom) =>
      sum + Math.max(0, classroom.capacity - classroom.enrolled),
    0,
  );

  return (
    <div className="live-workspace-page route-in">
      <WorkspaceHeader
        workspace={workspace}
        onBack={() => go('provider-access')}
        onSignOut={auth.signOut}
      />
      <main className="container live-workspace-content">
        {error && <div className="provider-access-error" role="alert">{error}</div>}
        {receipt && <div className="live-workspace-receipt" role="status">{receipt}</div>}
        <FreshnessPanel
          availability={workspace.availability}
          onConfirm={confirm}
          confirming={confirming}
        />
        <ProfileBasicsEditor
          profile={form.profile}
          onChange={updateProfile}
        />
        <ClassroomEditors
          classrooms={form.classrooms}
          onChange={updateClassroom}
          onRemove={(index) =>
            setForm((current) => ({
              ...current,
              classrooms: current.classrooms.filter(
                (_classroom, currentIndex) => currentIndex !== index,
              ),
            }))
          }
          onAdd={() =>
            setForm((current) => ({
              ...current,
              classrooms: [...current.classrooms, newClassroom()],
            }))
          }
        />
      </main>
      <PublishBar
        status={form.status}
        onStatusChange={(status) =>
          setForm((current) => ({ ...current, status }))
        }
        onPublish={publish}
        publishing={publishing}
        totalOpenings={totalOpenings}
      />
    </div>
  );
};
